// SavingsShowcase.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import "../styles/SavingsShowcase.css";


export default function SavingsShowcase({
    current = 1800000,
    goal = 6000000,
    targetDate = "28 May 2027",
}) {
    // progress calculations (clamped)
    const percent = Math.max(0, Math.min(100, Math.round((current / goal) * 100)));
    const radius = 90; // radius of the SVG circle
    const stroke = 14; // stroke width
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - percent / 100);
    const navigate = useNavigate();


    // helper for pretty currency
    const formatCurrency = (n) =>
        n.toLocaleString(undefined, { style: "currency", currency: "RMB", maximumFractionDigits: 0 });

    return (
        <section className="savings-section">
            <div className="savings-inner">
                {/* LEFT - card + decorative icons */}
                <div className="savings-left">
                    {/* Decorative floating boxes (replace SVGs with your assets if needed) */}
                    <div className="float-box f1" aria-hidden>
                        {/* small umbrella icon */}
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path d="M12 3c-4 0-7 3.134-7 7h14c0-3.866-3-7-7-7z" fill="#FDE8F2" />
                            <path d="M12 21a2 2 0 002-2v-3" stroke="#DD2E6E" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>

                    <div className="float-box f2" aria-hidden>
                        {/* suitcase */}
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <rect x="3" y="7" width="18" height="12" rx="2" fill="#F3E8FF" />
                            <path d="M8 7V5h8v2" stroke="#8B5CF6" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>

                    <div className="float-box f3" aria-hidden>
                        {/* car */}
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path d="M3 13l1.5-3h15L21 13" fill="#E6F7FF" />
                            <circle cx="7" cy="17" r="1.6" fill="#7DD3FC" />
                            <circle cx="17" cy="17" r="1.6" fill="#7DD3FC" />
                        </svg>
                    </div>

                    <div className="float-box f4" aria-hidden>
                        {/* house */}
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path d="M12 3l8 6v9a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1V9z" fill="#F3E8FF" />
                        </svg>
                    </div>

                    {/* Main progress card */}
                    <div className="progress-card" role="region" aria-label="Savings progress card">
                        <div className="progress-header">
                            <h3 className="progress-title">
                                Investing for a New Start Up
                            </h3>
                            <div className="target-date">
                                <small>Target end date</small>
                                <strong>{targetDate}</strong>
                            </div>
                        </div>

                        <div className="progress-body">
                            {/* SVG circular progress */}
                            <div className="progress-visual" aria-hidden>
                                <svg width={2 * (radius + stroke)} height={2 * (radius + stroke)} viewBox={`0 0 ${2 * (radius + stroke)} ${2 * (radius + stroke)}`}>
                                    <defs>
                                        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#FF6B9A" />
                                            <stop offset="100%" stopColor="#e5212d" />
                                        </linearGradient>
                                    </defs>

                                    {/* background ring */}
                                    <g transform={`translate(${radius + stroke}, ${radius + stroke})`}>
                                        <circle
                                            r={radius}
                                            stroke="#f0f0f0"
                                            strokeWidth={stroke}
                                            fill="transparent"
                                        />
                                        {/* progress ring */}
                                        <circle
                                            r={radius}
                                            stroke="url(#grad)"
                                            strokeWidth={stroke}
                                            strokeLinecap="round"
                                            fill="transparent"
                                            strokeDasharray={circumference}
                                            strokeDashoffset={offset}
                                            style={{ transition: "stroke-dashoffset 900ms ease" }}
                                            transform="rotate(-90)"
                                        />
                                    </g>
                                </svg>

                                {/* center text overlay */}
                                <div className="progress-center">
                                    Current amount
                                    <div className="current">{formatCurrency(current)}</div>
                                    <div className="divider" />
                                    <div className="goal">My goal<br /><strong>{formatCurrency(goal)}</strong></div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {/* RIGHT - hero text & CTA */}
                <div className="savings-right">
                    <div className="kicker">OUR SAVING MAGIC</div>
                    <h2 className="hero-titles">Invest and Save for the Future</h2>
                    <p className="hero-sub">The journey starts here — build micro-savings for real-life goals with a flexible plan and automated reminders.</p>
                    <div className="hero-cta-row">
                        <button
                            onClick={() => navigate("/login")}
                            className="cta-primary">Let's go</button>
                    </div>
                </div>
            </div>

            {/* Purple regulatory band */}
            <div className="regulated-band" aria-hidden>
                <div className="regulated-inner">
                    <div className="reg-left">
                        <small className="reg-kicker">ASIA’S LEADING DIGITAL BANK</small>
                        <h3 className="reg-title">Licensed and regulated by top financial authorities across Asia</h3>
                        <p className="reg-sub">
                            We operate under strict regulatory frameworks across key Asian financial hubs — ensuring your offshore
                            funds remain safe, compliant, and globally accessible.
                        </p>
                    </div>
                    <div className="reg-right">
                        <div className="reg-illustration">
                            <img src="/partner-7.png" alt="Asian digital banking illustration" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
